import React, { useEffect, useState } from 'react';

import { RowButton, SmallButton } from '@eika/button';
import { CurrencyInput, formatCurrencyValue } from '@eika/currency-input';
import { Icon, IconTypes } from '@eika/icon';
import { ModalDialogType } from '@eika/modal';

import useTranslation, { formatJsx } from '../../../hooks/useTranslation';
import { EikaModal } from '../../EikaModal';
import NoteFieldLayout from '../../NoteField/NoteFieldLayout';
import { StickyFooterRow } from '../../shared/StickyFooter';
import { validateAmountLimit } from './form/validate';

import './AmountLimitField.scss';

interface Props {
    transactionLimit: number;
    monthlyLimit: number | undefined;
    error?: { transaction?: string; monthly?: string };
    locked?: boolean;
    disabled?: boolean;
    onChange: (value: number) => void;
    onChangeMonthlyLimit: (value: number | undefined) => void;
    onShow?: (show: boolean) => void;
    animateModalAs?: ModalDialogType;
    icon?: IconTypes;
    externalOpen?: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
    className?: string;
}

type FocusFirstState = 'transaction' | 'monthly';

const AmountLimitField: React.FC<Props> = ({
    transactionLimit,
    monthlyLimit,
    error,
    onChange,
    onChangeMonthlyLimit,
    onShow,
    locked = false,
    disabled,
    animateModalAs,
    icon = IconTypes.LITEN_PIL_HOEYRE,
    externalOpen = [false, () => null],
    className,
}) => {
    const translated = useTranslation();
    const [showModal, setShowModal] = useState(false);
    const [value, setValue] = useState<number>(transactionLimit);
    const [monthlyAmountLimit, setMonthlyAmountLimit] = useState<number | undefined>(monthlyLimit);

    const [localTransactionLimitError, setLocalTransactionLimitError] = useState<string | undefined>();
    const [localMonthlyLimitError, setLocalMonthlyLimitError] = useState<string | undefined>();

    const [focusFirst, setFocusFirst] = useState<FocusFirstState>('transaction');

    const [external, setExternal] = externalOpen;

    const triggerModal = (show: boolean, focusFirst: FocusFirstState = 'transaction') => {
        setShowModal(show);
        onShow?.(show);
        setExternal(show);
        setFocusFirst(focusFirst);
    };

    useEffect(() => {
        setShowModal(external);
    }, [external]);

    useEffect(() => {
        setValue(transactionLimit);
    }, [transactionLimit]);

    useEffect(() => {
        setMonthlyAmountLimit(monthlyLimit);
    }, [monthlyLimit]);

    const onClose = () => {
        const validationResult = validateAmountLimit(translated, value, monthlyAmountLimit ?? 0);
        setLocalTransactionLimitError(validationResult.transactionLimit);
        setLocalMonthlyLimitError(validationResult.monthlyLimit);
        if (Object.entries(validationResult).some(([, value]) => value)) {
            return;
        }
        setFocusFirst('transaction');
        triggerModal(false);
        onChange(value);
        onChangeMonthlyLimit(monthlyAmountLimit);
    };

    const resetLocalError = () => {
        setLocalMonthlyLimitError(undefined);
        setLocalTransactionLimitError(undefined);
    };

    return (
        <>
            <li className={className}>
                <RowButton
                    onClick={() => triggerModal(true)}
                    disabled={disabled || locked}
                    className="amount-limit-field__row-btn"
                    data-testid="transactional-amount-limit"
                >
                    <NoteFieldLayout
                        placeholder={translated.avtaleGiroAgreement.limitAmountPlaceholder}
                        label={translated.avtaleGiroAgreement.limitAmountText}
                        note={transactionLimit ? `${formatCurrencyValue(transactionLimit)} kr` : ''}
                        icon={locked ? <Icon type={IconTypes.PIN_KODE_24} ariaLabel="Låst" /> : icon}
                        error={error?.transaction}
                    />
                </RowButton>
            </li>
            <li className={className}>
                <RowButton
                    onClick={() => triggerModal(true, 'monthly')}
                    disabled={disabled || locked}
                    className="amount-limit-field__row-btn"
                    data-testid="monthly-amount-limit"
                >
                    <NoteFieldLayout
                        placeholder={translated.avtaleGiroAgreement.monthlyLimitAmountPlaceholder}
                        label={translated.avtaleGiroAgreement.monthlyLimitAmountText}
                        note={monthlyLimit ? `${formatCurrencyValue(monthlyLimit)} kr` : ''}
                        icon={locked ? <Icon type={IconTypes.PIN_KODE_24} ariaLabel="Låst" /> : icon}
                        error={error?.monthly}
                    />
                </RowButton>
            </li>
            <EikaModal
                id="avtalegiroLimitField"
                open={showModal}
                setOpen={triggerModal}
                title={translated.avtaleGiroAgreement.maxAmount}
                onSubmit={onClose}
                animateAs={animateModalAs}
            >
                <p className="amount-limit__paragraph">{translated.avtaleGiroAgreement.limitAmountParagraph}</p>
                <CurrencyInput
                    label={translated.avtaleGiroAgreement.limitAmountText}
                    sublabel={translated.avtaleGiroAgreement.limitAmountDescription}
                    placeholder={translated.avtaleGiroAgreement.limitAmountPlaceholder}
                    maxLength={22}
                    inputMode="numeric"
                    type="text"
                    value={value}
                    error={error?.transaction ?? localTransactionLimitError}
                    name="avtalegiro-limit-field"
                    data-testid="transactional-amount-limit-input"
                    onChange={resetLocalError}
                    onValueChange={(e) => setValue(e.floatValue ?? 0)}
                    data-focus-first={focusFirst === 'transaction' ? true : undefined}
                />
                <section className="amount-limit__monthly-limit-wrapper">
                    <CurrencyInput
                        label={translated.avtaleGiroAgreement.monthlyLimitAmountText}
                        sublabel={
                            <span className="sgw-currency-input__sublabel">
                                {formatJsx(
                                    translated.avtaleGiroAgreement.monthlyLimitAmountDescription,
                                    <b>{translated.avtaleGiroAgreement.combined}</b>,
                                )}
                            </span>
                        }
                        placeholder={translated.avtaleGiroAgreement.monthlyLimitAmountPlaceholder}
                        maxLength={22}
                        inputMode="numeric"
                        type="text"
                        value={monthlyAmountLimit}
                        name="avtalegiro-monthly-limit-field"
                        data-testid="monthly-amount-limit-input"
                        onChange={resetLocalError}
                        onValueChange={(e) => setMonthlyAmountLimit(e.floatValue)}
                        data-focus-first={focusFirst === 'monthly' ? true : undefined}
                        error={error?.monthly ?? localMonthlyLimitError}
                    />
                </section>
                <StickyFooterRow>
                    <SmallButton onClick={onClose}>{translated.continue}</SmallButton>
                </StickyFooterRow>
            </EikaModal>
        </>
    );
};

export default AmountLimitField;
